import React, { useEffect, useMemo, useState } from "react";
import { all_routes } from "../../router/all_routes";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import { exportToCSV, exportToExcel, exportToPDF } from "../../../core/common/GlobalFunctions/exportTableData";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import { Button, Drawer, Form, Input, message, Upload } from "antd";
import { formatDate } from "../../../core/utils/configFormatters";
import { useAppConfig } from "../../../customhooks/useAppConfig";
import { decryptData } from "../../../core/utils/encryptUtils";
import { createCompanyDetails, fetchCompanyDetails, fetchCompanyDetailsById, updateCompanyDetails } from "../../../core/data/redux/company/companyDetailsActions";

// --- Regexes ---
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

// Full URL with http/https (no ftp), domain + optional path/query/hash
const URL_HTTP_REGEX = /^(https?:\/\/)([\w-]+\.)+[\w-]+(\/[^\s]*)?$/i;

// Bare domain only (no protocol, no path). Works for subdomains & TLDs like .com/.in/.co.in
const DOMAIN_REGEX =
    /^(?=.{1,253}$)(?!-)(?:[a-zA-Z0-9-]{1,63}\.)+[A-Za-z]{2,}$/;

// Indian GSTIN (15 chars)
const GSTIN_REGEX =
    /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/


// --- Custom validators (return Promise) ---
const validatePhone = (_: any, value?: string) => {
    if (!value) return Promise.resolve();
    const digits = value.replace(/\D/g, "");
    if (digits.length < 7 || digits.length > 15) {
        return Promise.reject(
            "Phone must contain 7–15 digits (you can include +, spaces, - or ())."
        );
    }
    if (!/^[+0-9()\-\s]*$/.test(value)) {
        return Promise.reject("Phone contains invalid characters.");
    }
    return Promise.resolve();
};

const validateDomain = (_: any, value?: string) => {
    if (!value) return Promise.resolve();
    if (/^https?:\/\//i.test(value)) {
        return Promise.reject("Enter domain without http(s):// (e.g., example.com).");
    }
    if (!DOMAIN_REGEX.test(value)) {
        return Promise.reject("Enter a valid domain (e.g., sub.example.co.in).");
    }
    return Promise.resolve();
};

const validateHttpUrl = (_: any, value?: string) => {
    if (!value) return Promise.resolve(); // optional fields pass if empty
    if (!URL_HTTP_REGEX.test(value)) {
        return Promise.reject("Enter a valid URL starting with http:// or https://");
    }
    return Promise.resolve();
};

const validateEmail = (_: any, value?: string) => {
    if (!value) return Promise.resolve();
    if (!EMAIL_REGEX.test(value)) {
        return Promise.reject("Enter a valid email address.");
    }
    return Promise.resolve();
};

const validateGSTIN = (_: any, value?: string) => {
    if (!value) return Promise.resolve();
    if (!GSTIN_REGEX.test(value)) {
        return Promise.reject("Enter a valid 15-character GSTIN.");
    }
    return Promise.resolve();
};


// ✅ Only these fields are required
const REQUIRED_FIELDS: string[] = [
    "plugin_logo",        // (Upload - validated manually)
    "company_name",
    "domain_name",
    "phone",
    "authorize_email",
    "address",
    "gst",
    "authorize_name",
    "authorize_mobile",
];

const NewCompany = () => {
    const dispatch = useDispatch<AppDispatch>();

    const [selectedDataStatus, setSelectedDataStatus] = useState("All");
    const [selectedSort, setSelectedSort] = useState<any>("");
    const [tableKey, setTableKey] = useState(0);
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [modalType, setModalType] = useState<"edit" | "create" | "">("");
    const [selectedRowData, setSelectedRowData] = useState<any>(null);

    const { user } = useSelector((state: any) => state.auth);
    const { roles } = useSelector((state: any) => state.role);
    const { createLoading, companyDetails, activeCompany } = useSelector((state: any) => state.companyDetails);

    const [form] = Form.useForm();

    useEffect(() => {
        dispatch(fetchCompanyDetails());
        dispatch(fetchCompanyDetailsById(Number(activeCompany?.company_detail_id)));
    }, [dispatch]);

    const resetFormData = (userId?: number) => ({
        company_detail_id: null,
        UserId: userId ?? null,
        company_image: "",        // optional
        plugin_logo: "",          // required (manual)
        domain_name: "",
        domain_enc_url: "",
        company_name: "",
        address: "",
        city: "",
        state: "",
        postal_code: "",
        country: "",
        phone: "",
        authorize_name: "",
        authorize_designation: "",
        authorize_email: "",
        authorize_sign: "",
        authorize_mobile: "",
        currency: "",             // optional
        fax: "",
        website: "",
        facebook: "",
        twitter: "",
        linkedin: "",
        financial: "0",
        gst: "",
    });

    const [formData, setFormData] = useState<any>(resetFormData(user?.UserId));
    const [errors, setErrors] = useState<any>({});

    // Prefill on edit (and sync AntD form)
    useEffect(() => {
        if (modalType === "edit" && selectedRowData) {
            const keys = [
                "company_detail_id",
                "UserId",
                "company_image",
                "plugin_logo",
                "domain_name",
                "domain_enc_url",
                "company_name",
                "address",
                "city",
                "state",
                "postal_code",
                "country",
                "phone",
                "authorize_name",
                "authorize_designation",
                "authorize_email",
                "authorize_sign",
                "authorize_mobile",
                "currency",
                "fax",
                "website",
                "facebook",
                "twitter",
                "linkedin",
                "financial",
                "gst",
            ];

            const filledData: any = {};
            keys.forEach((k) => {
                filledData[k] = selectedRowData?.[k] ?? "";
            });
            if (!filledData.UserId) filledData.UserId = user?.UserId ?? null;

            setFormData(filledData);
            form.setFieldsValue(filledData);
        }
    }, [modalType, selectedRowData, user?.UserId, form]);

    // Upload previews + store original files
    const handleImageChange = (file: any, type: "company_image" | "plugin_logo") => {
        const previewURL = URL.createObjectURL(file);
        setFormData((prev: any) => ({
            ...prev,
            [type]: previewURL,           // preview string for UI
            [`${type}_file`]: file,       // keep actual file for FormData
        }));
        return false;
    };

    // Input change (local state)
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Submit using FormData (same pattern as CompanyProfile)
    const handleSubmit = async () => {
        // manual check for required plugin_logo (Upload)
        const localErrors: any = {};
        if (!formData.plugin_logo && !formData.plugin_logo_file) {
            localErrors.plugin_logo = "Plugin logo is required";
        }
        setErrors(localErrors);
        if (Object.keys(localErrors).length) {
            message.error("Please add the required Plugin logo.");
            return;
        }

        try {
            // Validate required text fields (asterisks appear automatically)
            await form.validateFields();

            // Build FormData payload
            const payload = { ...form.getFieldsValue(true), ...formData };

            const fd = new FormData();
            Object.entries(payload).forEach(([key, value]) => {
                // Avoid appending transient preview URLs as files
                if (key.endsWith("_file") && value instanceof File) {
                    fd.append(key.replace("_file", ""), value);
                } else if (key !== "created_at" && key !== "updated_at" && value !== undefined && value !== null) {
                    // If preview URL exists and _file exists, skip the preview string
                    if ((key === "plugin_logo" || key === "company_image") && payload[`${key}_file`] instanceof File) {
                        return;
                    }
                    fd.append(key, value as any);
                }
            });

            if (modalType === "create") {
                await dispatch(createCompanyDetails(fd)).unwrap();
                message.success("Company created successfully");
                dispatch(fetchCompanyDetails());
                setIsDrawerVisible(false);
                const fresh = resetFormData(user?.UserId);
                setFormData(fresh);
                form.resetFields();
            } else {
                const company_detail_id = payload.company_detail_id;
                if (!company_detail_id) {
                    message.error("Company ID is missing, cannot update.");
                    return;
                }
                await dispatch(updateCompanyDetails({ company_detail_id, formData: fd })).unwrap();
                message.success("Update successful");
                setIsDrawerVisible(false);
                const fresh = resetFormData(user?.UserId);
                setFormData(fresh);
                form.resetFields();
                dispatch(fetchCompanyDetails());
            }
        } catch {
            // AntD will highlight invalid fields
        }
    };

    // Permissions
    const encryptedPermissions = localStorage.getItem("currentPermissions");
    const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || ""))
        ? decryptData(encryptedPermissions || "")
        : [];
    const canAdd = permissions.includes("Add") || permissions.includes("AllowAll");
    const canEdit = permissions.includes("Edit") || permissions.includes("AllowAll");

    // Open drawer helpers
    const showDrawer = (type: "edit" | "create") => {
        setModalType(type);
        setIsDrawerVisible(true);
    };

    const handleEdit = (record: any): void => {
        setSelectedRowData(record);
        showDrawer("edit"); // ensure record is set before open (avoids race)
    };

    // Listing data
    const data = companyDetails ? companyDetails : [];
    const filteredData = useMemo(() => {
        const filters = selectedDataStatus && selectedDataStatus !== "All" ? { Status: selectedDataStatus } : {};
        return filterData(data || [], filters, "CreatedAt", "CreatedAt", selectedSort);
    }, [roles, selectedDataStatus, selectedSort, companyDetails, activeCompany]);

    const CompanytData = filteredData?.map((item: any) => {
        const replaceUndefined = (value: any) => (value === "undefined" ? "" : value);
        const filteredItem = Object.fromEntries(
            Object.entries({
                company_name: replaceUndefined(item.company_name),
                gst: replaceUndefined(item.gst),
                plugin_logo: replaceUndefined(item.plugin_logo),
                domain_name: replaceUndefined(item.domain_name),
                address: replaceUndefined(item.address),
                city: replaceUndefined(item.city),
                state: replaceUndefined(item.state),
                country: replaceUndefined(item.country),
                phone: replaceUndefined(item.phone),
                authorize_name: replaceUndefined(item.authorize_name),
                authorize_designation: replaceUndefined(item.authorize_designation),
                authorize_email: replaceUndefined(item.authorize_email),
                authorize_sign: replaceUndefined(item.authorize_sign),
                authorize_mobile: replaceUndefined(item.authorize_mobile),
                website: replaceUndefined(item.website),
                facebook: replaceUndefined(item.facebook),
                twitter: replaceUndefined(item.twitter),
                linkedin: replaceUndefined(item.linkedin),
            })
                .filter(([key, value]) => value !== "" && value != null) // Filter out empty or null values
        );
        return filteredItem;
    });

    useEffect(() => {
        setTableKey((prevKey) => prevKey + 1);
    }, [selectedDataStatus, selectedSort, roles, companyDetails, activeCompany]);

    const config = useAppConfig();
    if (!config) return null;

    const columns = [
        {
            title: "Company Name",
            dataIndex: "company_name",
            sorter: (a: any, b: any) => a.company_name.length - b.company_name.length,
        },
        {
            title: "Created Date",
            dataIndex: "created_at",
            render: (text: string) => formatDate(text, config),
            sorter: (a: any, b: any) => a.created_at?.length - b.created_at?.length,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text: any, record: any) => {
                const activeCompanyName = activeCompany?.company_name;
                const isActive = record.company_name === activeCompanyName;
                return (
                    <span style={{ color: isActive ? "green" : "red", fontWeight: 600 }}>
                        {isActive ? "Currently Running" : "Not In Use"}
                    </span>
                );
            },
        },
        {
            title: "Action",
            dataIndex: "actions",
            render: (text: string, record: any) => (
                <div className="action-icon d-inline-flex">
                    <button
                        className="me-2"
                        onClick={() => {
                            if (!canEdit) return;
                            handleEdit(record);
                        }}
                        disabled={!canEdit}
                        style={{
                            cursor: canEdit ? "pointer" : "not-allowed",
                            opacity: canEdit ? 1 : 0.5,
                        }}
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Edit"
                    >
                        <i className="ti ti-edit" />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Add Company</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={all_routes.adminDashboard}>
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">Administration</li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Company
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                            <div className="me-2 mb-2">
                                <div className="dropdown">
                                    <Link to="#" className="dropdown-toggle btn btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                                        <i className="ti ti-file-export me-1" />
                                        Export
                                    </Link>
                                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                                        <li>
                                            <button onClick={() => exportToCSV(CompanytData, "Company_data")} className="dropdown-item rounded-1">
                                                <i className="ti ti-file-type-pdf me-1" />
                                                Export as CSV
                                            </button>
                                        </li>
                                        <li>
                                            <button onClick={() => exportToExcel(CompanytData, "Company_data")} className="dropdown-item rounded-1">
                                                <i className="ti ti-file-type-xls me-1" />
                                                Export as Excel{" "}
                                            </button>
                                        </li>
                                        <li>
                                            <button onClick={() => exportToPDF(CompanytData, "Company_data")} className="dropdown-item rounded-1">
                                                <i className="ti ti-file-type-xls me-1" />
                                                Export as PDF{" "}
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="mb-2">
                                <button
                                    onClick={() => {
                                        setErrors({});
                                        const fresh = resetFormData(user?.UserId);
                                        setFormData(fresh);
                                        form.resetFields();
                                        form.setFieldsValue(fresh);
                                        setSelectedRowData(null);
                                        showDrawer("create");
                                    }}
                                    className="btn btn-primary d-flex align-items-center"
                                >
                                    <i className="ti ti-circle-plus me-2" />
                                    Add New Company
                                </button>
                            </div>
                            <div className="head-icons ms-2">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>
                    {/* /Breadcrumb */}

                    {/* Assets Lists */}
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            <h5>Company List</h5>
                            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                                <div className="dropdown">
                                    <Link to="#" className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                                        {selectedSort ? `Sort By : ${selectedSort}` : "Sort By : Last 7 Days"}
                                    </Link>
                                    <ul className="dropdown-menu dropdown-menu-end p-3">
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Recently Added")} className="dropdown-item rounded-1">
                                                Recently Added
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Ascending")} className="dropdown-item rounded-1">
                                                Ascending
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Descending")} className="dropdown-item rounded-1">
                                                Descending
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Last Month")} className="dropdown-item rounded-1">
                                                Last Month
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Last 7 Days")} className="dropdown-item rounded-1">
                                                Last 7 Days
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            <Table key={tableKey} dataSource={filteredData} columns={columns} Selection={true} />
                        </div>
                    </div>
                </div>
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>
            {/* /Page Wrapper */}

            {/* Drawer */}
            <Drawer
                title={modalType === "edit" ? "Edit Company" : "Create Company"}
                placement="right"
                width={600}
                style={{ marginTop: 50 }}
                onClose={() => setIsDrawerVisible(false)}
                open={isDrawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
                destroyOnClose
            >
                <Form form={form} layout="vertical" requiredMark>
                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item label="Company Image">{/* optional */}
                                <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleImageChange(file, "company_image")}>
                                    <Button type="primary">
                                        {formData.company_image || formData.company_image_file ? "Change Image" : "Select Image"}
                                    </Button>
                                </Upload>
                            </Form.Item>
                            {formData.company_image && (
                                <div>
                                    <img
                                        src={
                                            typeof formData.company_image === "string"
                                                ? formData.company_image
                                                : URL.createObjectURL(formData.company_image)
                                        }
                                        alt="Company"
                                        style={{ width: 150, height: 150, marginTop: 10, objectFit: "contain" }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="col-md-6">
                            <Form.Item
                                label={
                                    <span>
                                        Plugin Logo <span style={{ color: "red" }}>*</span>
                                    </span>
                                }
                                help={errors.plugin_logo}
                                validateStatus={errors.plugin_logo ? "error" : ""}
                            >
                                <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleImageChange(file, "plugin_logo")}>
                                    <Button type="primary">
                                        {formData.plugin_logo || formData.plugin_logo_file ? "Change Logo" : "Select Logo"}
                                    </Button>
                                </Upload>
                            </Form.Item>
                            {formData.plugin_logo && (
                                <div>
                                    <img
                                        src={
                                            typeof formData.plugin_logo === "string"
                                                ? formData.plugin_logo
                                                : URL.createObjectURL(formData.plugin_logo)
                                        }
                                        alt="Plugin Logo"
                                        style={{ width: 150, height: 150, marginTop: 10, objectFit: "contain" }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Text Inputs — asterisk appears for those with required rule */}
                    {/* Text Inputs — asterisk appears for those with required rule */}
                    {[
                        // --- Required fields ---
                        {
                            label: "Company Name",
                            name: "company_name",
                            required: true,
                            rules: [],
                        },
                        {
                            label: "Domain Name",
                            name: "domain_name",
                            required: true,
                            rules: [
                                { transform: (v:any) => String(v ?? "").trim() },
                                { validator: validateDomain },
                            ],
                        },
                        {
                            label: "Phone",
                            name: "phone",
                            required: true,
                            rules: [
                                { transform: (v:any) => String(v ?? "").trim() },
                                { validator: validatePhone },
                            ],
                        },
                        {
                            label: "Email",
                            name: "authorize_email",
                            required: true,
                            rules: [
                                { transform: (v:any) => String(v ?? "").trim() },
                                { validator: validateEmail },
                            ],
                        },
                        {
                            label: "Address",
                            name: "address",
                            required: true,
                            rules: [{ transform: (v: any) => String(v ?? "").trim() }],
                        },
                        {
                            label: "GST",
                            name: "gst",
                            required: true,
                            rules: [
                                { transform: (v:any) => String(v ?? "").toUpperCase().trim() },
                                { validator: validateGSTIN },
                            ],
                        },
                        {
                            label: "Authorized Person",
                            name: "authorize_name",
                            required: true,
                            rules: [{ transform: (v:any) => String(v ?? "").trim() }],
                        },
                        {
                            label: "Mobile",
                            name: "authorize_mobile",
                            required: true,
                            rules: [
                                { transform: (v:any) => String(v ?? "").trim() },
                                { validator: validatePhone },
                            ],
                        },

                        // --- Optional fields (validate only when not empty) ---
                        {
                            label: "Currency",
                            name: "currency",
                            required: false,
                            rules: [{ transform: (v: any) => String(v ?? "").trim() }],
                        },
                        {
                            label: "Designation",
                            name: "authorize_designation",
                            required: false,
                            rules: [{ transform: (v: any) => String(v ?? "").trim() }],
                        },
                        {
                            label: "Website",
                            name: "website",
                            required: false,
                            rules: [
                                { transform: (v: any) => String(v ?? "").trim() },
                                { validator: validateHttpUrl },
                            ],
                        },
                        {
                            label: "Facebook",
                            name: "facebook",
                            required: false,
                            rules: [
                                { transform: (v: any) => String(v ?? "").trim() },
                                { validator: validateHttpUrl },
                            ],
                        },
                        {
                            label: "Twitter / X",
                            name: "twitter",
                            required: false,
                            rules: [
                                { transform: (v: any) => String(v ?? "").trim() },
                                { validator: validateHttpUrl },
                            ],
                        },
                        {
                            label: "LinkedIn",
                            name: "linkedin",
                            required: false,
                            rules: [
                                { transform: (v: any) => String(v ?? "").trim() },
                                { validator: validateHttpUrl },
                            ],
                        },
                    ].map(({ label, name, required, rules }) => (
                        <Form.Item
                            key={name}
                            label={label}
                            name={name}
                            rules={
                                ([
                                    ...(required ? [{ required: true, message: `${label} is required` }] : []),
                                    ...rules,                
                                ] as any)  
                            }

                        >
                            <Input name={name} value={(formData as any)[name]} onChange={handleInputChange} />
                        </Form.Item>
                    ))}

                    <div className="text-center mt-4">
                        <Button type="primary" onClick={handleSubmit} disabled={createLoading}>
                            {createLoading && <span className="spinner-border spinner-border-sm me-2" />}
                            {modalType === "edit" ? "Save Changes" : "Create Company"}
                        </Button>
                    </div>
                </Form>
            </Drawer>
            {/* /Drawer */}

            <DeleteModal1
                isVisible={false /* kept for parity; wire it if needed */}
                onClose={() => { }}
                onConfirm={() => { }}
            />

            <style>
                {`
          .role-input-box {
            border: 1px solid #8c8c8c !important; 
            border-radius: 4px;
            background-color: #fff !important;     
            color: #000;
            box-shadow: none !important;
            outline: none !important;
          }
          .role-input-status {
            background-color: #fff !important;     
            color: #000;
            box-shadow: none !important;
            outline: none !important;
          }

          .role-input-box:focus,
          .role-input-box:focus-within {
            border-color: #8c8c8c !important;    
            box-shadow: none !important;           
            background-color: #fff !important;     
          }

          .role-input-box:focus-visible {
            outline: none !important;
          }
        `}
            </style>
        </>
    );
};

export default NewCompany;
